import React, { useCallback, useEffect, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

// How far in from an edge the current tab is kept, so it never sits under the fade.
const EDGE_MARGIN = 28;

/**
 * Which ends of the tab row have more behind them. Pure, so the rule is
 * testable without a layout engine: a row that fits (within a pixel) fades
 * nowhere; one that does not fades at an end only while it can still scroll
 * that way.
 */
export function fadeState({ scrollLeft = 0, clientWidth = 0, scrollWidth = 0 } = {}) {
	const room = scrollWidth - clientWidth;
	if (!(room > 1)) return { start: false, end: false };
	return { start: scrollLeft > 1, end: scrollLeft < room - 1 };
}

/**
 * Scroll the row, not the page, until `tab` is inside it and clear of the
 * fades. Setting scrollLeft on the row is deliberate: `scrollIntoView` would
 * also scroll the window, and the header is pinned above the content.
 */
export function revealTab(row, tab) {
	if (!row || !tab) return;
	const rowBox = row.getBoundingClientRect();
	const tabBox = tab.getBoundingClientRect();
	const left = tabBox.left - rowBox.left + row.scrollLeft;
	const right = left + tabBox.width;
	if (left < row.scrollLeft + EDGE_MARGIN) row.scrollLeft = Math.max(0, left - EDGE_MARGIN);
	else if (right > row.scrollLeft + row.clientWidth - EDGE_MARGIN) row.scrollLeft = right - row.clientWidth + EDGE_MARGIN;
}

/**
 * WHAT CAN I DO HERE (§13.2): the scope's sibling sections in their own row,
 * as links. When they do not fit they scroll sideways, the ends that have more
 * behind them fade (`data-fade-start` / `data-fade-end`, drawn by
 * shell-header.css), and the current tab is always brought into view.
 */
export default function ShellTabs({ tabs = [], label = 'Collection sections' }) {
	const rowRef = useRef(null);
	const { pathname } = useLocation();
	const [fade, setFade] = useState({ start: false, end: false });

	const measure = useCallback(() => {
		const row = rowRef.current;
		if (!row) return;
		const next = fadeState(row);
		setFade(prev => (prev.start === next.start && prev.end === next.end ? prev : next));
	}, []);

	useEffect(() => {
		const row = rowRef.current;
		if (!row) return undefined;
		measure();
		row.addEventListener('scroll', measure, { passive: true });
		window.addEventListener('resize', measure);
		const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
		observer?.observe(row);
		return () => {
			row.removeEventListener('scroll', measure);
			window.removeEventListener('resize', measure);
			observer?.disconnect();
		};
	}, [measure, tabs.length]);

	// The current tab, on arrival and whenever the route (so the current tab) changes.
	useEffect(() => {
		const row = rowRef.current;
		revealTab(row, row?.querySelector('[aria-current="page"]'));
		measure();
	}, [pathname, tabs.length, measure]);

	if (!tabs.length) return null;
	return (
		<nav
			className="shell-header__tabs"
			aria-label={label}
			data-shell-tabs
			data-fade-start={fade.start ? 'true' : undefined}
			data-fade-end={fade.end ? 'true' : undefined}
		>
			<div className="context-tabs" ref={rowRef}>
				{tabs.map(tab => (
					<NavLink
						key={tab.key}
						to={tab.to}
						end={tab.end}
						data-shell-tab={tab.key}
						className={({ isActive }) => `context-tabs__tab${isActive ? ' context-tabs__tab--active' : ''}`}
					>
						{tab.label}
					</NavLink>
				))}
			</div>
		</nav>
	);
}
